import React, { useEffect, useState } from 'react'
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Space,
  Button,
  Tag,
  Progress,
  Tabs,
  Avatar,
  Badge,
  Select,
  Tooltip,
  DatePicker,
  Skeleton,
} from 'antd'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  CalendarOutlined,
  EyeOutlined,
  FireOutlined,
  TrophyOutlined,
  AlertOutlined,
  ReloadOutlined,
  ExportOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import { useMasterData } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import { LeadStage, ProposalStatus } from '@/types'
import { leadStore } from '@/pages/Lead/leadStore'
import { invoiceStore } from '@/pages/Invoice/invoiceStore'

import { payoutStore } from '@/pages/Payout/payoutStore'
import { proposalStore } from '@/pages/Proposal/proposalStore'
import { agreementStore } from '@/pages/Agreement/agreementStore'
import { notificationStore, isUnread } from '@/pages/Notification/notificationStore'
import { meterReadingStore } from '@/pages/MeterReading/meterReadingStore'
import { allocationStore } from '@/pages/Allocation/allocationStore'
import { notification } from 'antd'
import RevenueAnalytics from './RevenueAnalytics'
import './Dashboard.css'

const { Option } = Select

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const [periodMode, setPeriodMode] = useState<'month' | 'fy' | 'custom'>('month')
  const [powerTypeFilter, setPowerTypeFilter] = useState('all')
  const [plant, setPlant] = useState('all')           // BRANCH-equivalent: filter by plant / generator
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('overview')

  // Master data drives the real generator / consumer pending-units figures
  const { generators, consumers, employees, powerTypes, masterDataLoaded } = useMasterData()

  // True until every store this page reads from has finished its first API
  // fetch. Rendering real figures before then would show ₹0/empty content
  // that then jumps to the real numbers — show a spinner instead.
  const dashboardLoading = !masterDataLoaded
    || !leadStore.loaded
    || !invoiceStore.loaded
    || !payoutStore.loaded
    || !proposalStore.loaded
    || !agreementStore.loaded
    || !meterReadingStore.loaded
    || !allocationStore.loaded

  // Re-render whenever any API-backed store this page reads from refreshes or
  // changes. invoiceStore/payoutStore/proposalStore/agreementStore/
  // notificationStore all start their in-memory cache empty and hydrate
  // asynchronously (App.tsx's refreshAllStores(), right after login) — without
  // subscribing to their events too, the dashboard's first render reads them
  // while still empty (₹0 revenue, empty Financial/Approvals/Team tabs) and
  // then never updates once the real data actually arrives, since nothing
  // this component listens for fires when THOSE specific stores finish
  // loading.
  const [, forceTick] = useState(0)
  useEffect(() => {
    const handleUpdate = () => forceTick((n) => n + 1)
    const storesToWatch = [
      meterReadingStore, allocationStore,
      invoiceStore, payoutStore, proposalStore, agreementStore, notificationStore,
    ]
    storesToWatch.forEach(s => window.addEventListener(s.eventName, handleUpdate))

    const unsubs = [
      leadStore.subscribe(() => forceTick((n) => n + 1)),
    ]
    return () => {
      unsubs.forEach(unsub => unsub())
      storesToWatch.forEach(s => window.removeEventListener(s.eventName, handleUpdate))
    }
  }, [])

  // ── Greeting + reference-style header bits ────────────────────────────
  const now = dayjs()
  const currentFyStart = now.month() >= 3 ? now.year() : now.year() - 1
  const [selectedFY, setSelectedFY] = useState<number>(currentFyStart)
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs]>([now.subtract(29, 'day'), now])
  const hour = now.hour()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const userName = (authStore.getUser()?.name || 'User').split(' ')[0]

  const customDays = Math.max(1, customRange[1].diff(customRange[0], 'day') + 1)
  const periodTitle =
    periodMode === 'fy' ? `FY ${selectedFY}-${String(selectedFY + 1).slice(-2)}`
    : periodMode === 'custom' ? `${customRange[0].format('DD MMM')} – ${customRange[1].format("DD MMM 'YY")}`
    : now.format('MMM YYYY')
  const periodLabel =
    periodMode === 'fy' ? `FY ${selectedFY}-${String(selectedFY + 1).slice(-2)}`
    : periodMode === 'custom' ? 'custom range'
    : 'this month'

  // Actual date range for the selected period — single source of truth shared
  // with the Revenue Analytics section so the whole dashboard uses one filter.
  const dashRange: [Dayjs, Dayjs] =
    periodMode === 'fy'
      ? [dayjs(`${selectedFY}-04-01`).startOf('day'), dayjs(`${selectedFY + 1}-03-31`).endOf('day')]
      : periodMode === 'custom'
      ? [customRange[0].startOf('day'), customRange[1].endOf('day')]
      : [now.startOf('month'), now.endOf('month')]

  // ── Real figures pulled from the data stores (dashboard mirrors the modules) ──
  const matchingGenerators = generators.filter(g => (plant === 'all' || g.id === plant) && (powerTypeFilter === 'all' || g.typeOfPower?.includes(powerTypeFilter)))
  const matchingConsumers = powerTypeFilter === 'all' ? consumers : consumers.filter(c => c.typeOfPower?.includes(powerTypeFilter))
  const matchingEntityIds = new Set([...matchingGenerators.map(g => g.id), ...matchingConsumers.map(c => c.id)])

  // Bypass only when NEITHER filter narrows anything — previously this only
  // checked powerTypeFilter, so picking a specific Plant/Generator while
  // leaving Generation Type at "All Types" silently had zero effect on the
  // Invoices/Payouts figures (matchingEntityIds was correctly narrowed by
  // `plant`, but never consulted).
  const noEntityFilter = plant === 'all' && powerTypeFilter === 'all'
  const invoices     = invoiceStore.getAll().filter(i => noEntityFilter || (i.entityId && matchingEntityIds.has(i.entityId)))
  const payouts      = payoutStore.getAll().filter(p => noEntityFilter || (p.generatorId && matchingEntityIds.has(p.generatorId)))
  const leads        = leadStore.getLeads().filter(l => powerTypeFilter === 'all' || l.typeOfPower === powerTypeFilter)
  const proposalsAll = proposalStore.getAll()
  const agreements   = agreementStore.getAll()

  // ── Reminders ──
  const agreementProposalIds = new Set(agreements.map(a => a.proposalId))
  const uncreatedAgreements = proposalsAll.filter(p => 
    p.status === ProposalStatus.APPROVED && !agreementProposalIds.has(p.id)
  )
  const waitingAgreements = agreements.filter(a => 
    a.status === 'Draft' || a.status === 'Ready for Signing' || a.status === 'Pending Scan Upload'
  )

  useEffect(() => {
    const hasAgreements = uncreatedAgreements.length > 0 || waitingAgreements.length > 0
    const DAY_MS = 24 * 60 * 60 * 1000
    const nowTime = Date.now()

    // Find leads with follow-ups due today or overdue
    const dueFollowups = leads.filter(l => {
      if (!l.discussionHistory || l.discussionHistory.length === 0) return false
      const sorted = [...l.discussionHistory]
        .filter(d => d.nextFollowUpDate)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      if (sorted.length === 0) return false
      
      const latest = sorted[0]
      const dueTime = new Date(latest.nextFollowUpDate!).getTime()
      return dueTime <= nowTime
    })

    // Leads still missing their HTSC Number and/or GST Number, split by whether
    // the 2-day update window from lead creation is still open. Both get a
    // notification: the reminder is the whole point of the 2-day window, and
    // the overdue one keeps nagging after it lapses (see
    // computeLeadDeactiveState in utils/gst.ts). Neither blocks the lead.
    const pendingLeads = leads.filter(l => l.gstStatus === 'pending' && !l.isDeactive)
    const deactiveLeads = leads.filter(l => l.isDeactive)

    if (!hasAgreements && dueFollowups.length === 0 && pendingLeads.length === 0 && deactiveLeads.length === 0) return

    notificationStore.refresh().then(() => {
      const existing = notificationStore.getAll()

      // 1. Pending Agreements Notification
      if (hasAgreements) {
        const title = 'Action Required: Pending Agreements'
        const msg = `You have ${uncreatedAgreements.length} pending agreements to create and ${waitingAgreements.length} agreements waiting for sign/execution.`
        const recentDuplicate = existing.some(n =>
          n.title === title &&
          (isUnread(n.status) || (nowTime - new Date(n.createdAt).getTime()) < DAY_MS)
        )
        if (!recentDuplicate) {
          notificationStore.save({
            eventTrigger: 'SYSTEM_ALERT',
            title,
            message: msg,
            recipient: 'All',
            channels: ['In-App'],
            status: 'Unread',
            createdAt: new Date().toISOString()
          })
        }
      }

      // 2. Upcoming Follow-ups Notifications (Per Lead)
      dueFollowups.forEach(lead => {
        const title = `Follow-up Due: ${lead.companyName}`
        const msg = `You have a follow-up due for ${lead.companyName} (${lead.assignedTo || 'Unassigned'}).|||LEAD_ID:${lead.id}`
        const recentDuplicate = existing.some(n =>
          n.title === title &&
          (isUnread(n.status) || (nowTime - new Date(n.createdAt).getTime()) < DAY_MS)
        )
        if (!recentDuplicate) {
          notificationStore.save({
            eventTrigger: 'SYSTEM_ALERT',
            title,
            message: msg,
            recipient: 'All',
            channels: ['In-App'],
            status: 'Unread',
            createdAt: new Date().toISOString()
          })
        }
      })

      // 3. HTSC/GST Reminder — the 2-day window is still open. This is the
      // reminder the window exists for: it goes out while there's still time
      // to act, not only once the deadline has already been missed.
      pendingLeads.forEach(lead => {
        const missing = (lead.missingFields || ['HTSC Number', 'GST Number']).join(' & ')
        const title = `Reminder — ${missing} due: ${lead.companyName}`
        const msg = `${lead.companyName} still needs its ${missing}. Due ${lead.gstVerifyDueDate || '—'} (2 days from lead creation)${typeof lead.daysLeft === 'number' ? ` — ${lead.daysLeft} day(s) left` : ''}. Assigned to: ${lead.assignedTo || 'Unassigned'}.|||LEAD_ID:${lead.id}`
        const recentDuplicate = existing.some(n =>
          n.title === title &&
          (isUnread(n.status) || (nowTime - new Date(n.createdAt).getTime()) < DAY_MS)
        )
        if (!recentDuplicate) {
          notificationStore.save({
            eventTrigger: 'SYSTEM_ALERT',
            title,
            message: msg,
            recipient: lead.assignedTo || 'All',
            channels: ['In-App'],
            status: 'Unread',
            createdAt: new Date().toISOString()
          })
        }
      })

      // 4. HTSC/GST still missing after the window closed (Per Lead) — the
      // lead carries a red mark in the Lead list, but this is what actually
      // pushes it to whoever's responsible instead of relying on someone
      // spotting the mark. The lead itself stays fully usable.
      deactiveLeads.forEach(lead => {
        const missing = (lead.missingFields || ['HTSC Number', 'GST Number']).join(' & ')
        const title = `Overdue — ${missing} missing: ${lead.companyName}`
        const msg = `${lead.companyName} is still missing its ${missing} — the 2-day update window closed on ${lead.gstVerifyDueDate || '—'}. Assigned to: ${lead.assignedTo || 'Unassigned'}.|||LEAD_ID:${lead.id}`
        const recentDuplicate = existing.some(n =>
          n.title === title &&
          (isUnread(n.status) || (nowTime - new Date(n.createdAt).getTime()) < DAY_MS)
        )
        if (!recentDuplicate) {
          notificationStore.save({
            eventTrigger: 'SLA_BREACH',
            title,
            message: msg,
            recipient: lead.assignedTo || 'All',
            channels: ['In-App'],
            status: 'Unread',
            createdAt: new Date().toISOString()
          })
        }
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uncreatedAgreements.length, waitingAgreements.length, leads.length])

  const ageDays = (d: string | Date) => Math.max(0, Math.round((now.valueOf() - dayjs(d).valueOf()) / 86_400_000))
  const LEAD_VALUE_PER_KW = 50_000
  const leadValue = (l: { sanctionedDemand?: number }) => (l.sanctionedDemand || 0) * LEAD_VALUE_PER_KW
  const stageKey = (s: LeadStage) => String(s).toLowerCase()

  const totalRevenue     = invoices.reduce((s, i) => s + Number(i.baseAmount || 0), 0)
  const totalInvoiced    = invoices.reduce((s, i) => s + Number(i.totalAmount || 0), 0)
  const totalCollected   = invoices.reduce((s, i) => s + Number(i.paidAmount || 0), 0)
  const totalOutstanding = invoices.reduce((s, i) => s + Math.max(0, Number(i.totalAmount || 0) - Number(i.paidAmount || 0)), 0)

  const collectionRate   = totalInvoiced ? Math.round((totalCollected / totalInvoiced) * 100) : 0
  const unpaidCount      = invoices.filter(i => (i.paidAmount || 0) < (i.totalAmount || 0)).length
  const overdueAmount    = invoices
    .filter(i => (i.paidAmount || 0) < (i.totalAmount || 0) && ageDays(i.dueDate) > 30)
    .reduce((s, i) => s + (Number(i.totalAmount || 0) - Number(i.paidAmount || 0)), 0)
  // ── KPI cards (real, computed from invoices) ─────────────────
  const kpis = [
    { key: 'revenue',     label: 'Total Revenue',    value: totalRevenue,     prefix: '₹', change: 0, color: '#6366f1', bg: 'linear-gradient(135deg,#6366f1 0%,#818cf8 100%)', icon: <DollarOutlined />,      sub: `${invoices.length} invoices · pre-GST` },
    { key: 'invoiced',    label: 'Invoiced Amount',  value: totalInvoiced,    prefix: '₹', change: 0, color: '#0ea5e9', bg: 'linear-gradient(135deg,#0ea5e9 0%,#38bdf8 100%)', icon: <FileTextOutlined />,    sub: 'Incl. GST' },
    { key: 'collected',   label: 'Amount Collected', value: totalCollected,   prefix: '₹', change: 0, color: '#10b981', bg: 'linear-gradient(135deg,#10b981 0%,#34d399 100%)', icon: <CheckCircleOutlined />, sub: `${collectionRate}% collection rate` },
    { key: 'outstanding', label: 'Outstanding',      value: totalOutstanding, prefix: '₹', change: 0, color: '#f59e0b', bg: 'linear-gradient(135deg,#f59e0b 0%,#fbbf24 100%)', icon: <ClockCircleOutlined />, sub: `${unpaidCount} unpaid invoice${unpaidCount === 1 ? '' : 's'}` },
  ]

  // ── Lead metrics (real, from leadStore) ───────────────────────────────
  const totalLeads     = leads.length
  const hotLeads       = leads.filter(l => l.stage === LeadStage.HOT).length
  const convertedLeads = leads.filter(l => l.convertedToProposal || l.stage === LeadStage.WON).length
  const lostLeads      = leads.filter(l => l.stage === LeadStage.LOST).length
  const conversionRate = totalLeads ? Math.round((convertedLeads / totalLeads) * 100) : 0

  // ── Live generation / allocation data (real, from the stores) ─────────
  // Every figure below is computed straight from Meter Readings + Allocations
  // — no synthetic rates — scoped to the selected Period and Generation Type.
  const allReadings = meterReadingStore.getAll()
  const allAllocations = allocationStore.getAll()

  const inDashRange = (dateStr?: string | Date) => {
    if (!dateStr) return false
    const d = dayjs(dateStr)
    return d.isValid() && !d.isBefore(dashRange[0]) && !d.isAfter(dashRange[1])
  }
  // Allocation "period" is NOT one consistent format — AllocationForm both
  // auto-fills it from a meter reading ("2026-07-01 – 2026-07-31") and lets
  // the user free-type it (real values in the DB include "Aug 2026",
  // "JUL 2026", "July 2026", plus "—" for none set). Only handling the first
  // shape used to silently drop every free-typed one from every period-based
  // figure below (KPI cards, Pending Units for Sale, Generation Trend) —
  // regardless of which month was actually selected.
  const MONTH_ABBR = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
  const allocDate = (period?: string) => {
    if (!period) return null
    const p = period.trim()
    const rangeMatch = p.match(/^(\d{4}-\d{2}-\d{2})\s*[–-]\s*\d{4}-\d{2}-\d{2}$/)
    if (rangeMatch) return dayjs(rangeMatch[1])
    const monthYearMatch = p.match(/^([A-Za-z]+)\s+(\d{4})$/)
    if (monthYearMatch) {
      const mi = MONTH_ABBR.indexOf(monthYearMatch[1].slice(0, 3).toLowerCase())
      if (mi !== -1) return dayjs(`${monthYearMatch[2]}-${String(mi + 1).padStart(2, '0')}-01`)
      return null
    }
    const d = dayjs(p)
    return d.isValid() ? d : null
  }
  const inDashRangeAlloc = (period?: string) => {
    const d = allocDate(period)
    return !!d && d.isValid() && !d.isBefore(dashRange[0]) && !d.isAfter(dashRange[1])
  }

  const genReadings  = allReadings.filter(r => r.type === 'generator' && inDashRange(r.startDate))
  const consReadings = allReadings.filter(r => r.type === 'consumer'  && inDashRange(r.startDate))
  const allocInRange = allAllocations.filter(a => inDashRangeAlloc(a.period))

  // ── Pending Units for Sale ────────────────────────────────────────────
  // Generator: how much it generated (meter readings) vs how much was allocated.
  // Consumer:  how much it needs (its own meter readings) vs how much was supplied.
  const genUnitRows = matchingGenerators
    .filter(g => g.status === 'active' && (plant === 'all' || g.id === plant))
    .map(g => {
      const generated = genReadings.filter(r => r.entityId === g.id).reduce((s, r) => s + (Number(r.totalUnits) || 0), 0)
      const sold      = allocInRange.filter(a => a.generatorId === g.id).reduce((s, a) => s + (Number(a.totalAllocated) || 0), 0)
      const pending   = Math.max(0, generated - sold)
      return { id: g.id, name: g.name, generated, sold, pending, pct: generated ? Math.round((pending / generated) * 100) : 0 }
    })
  const consUnitRows = matchingConsumers
    .filter(c => c.status === 'active')
    .map(c => {
      const required = consReadings.filter(r => r.entityId === c.id).reduce((s, r) => s + (Number(r.totalUnits) || 0), 0)
      const received = allocInRange.reduce((s, a) =>
        s + (a.consumerAllocations || []).filter(ca => ca.id === c.id).reduce((ss, ca) => ss + (Number(ca.allocatedUnits) || 0), 0), 0)
      const pending  = Math.max(0, required - received)
      return { id: c.id, name: c.name, required, received, pending, pct: required ? Math.round((pending / required) * 100) : 0 }
    })
  const totalGenerated      = genUnitRows.reduce((s, r) => s + Number(r.generated), 0)
  const totalSold           = genUnitRows.reduce((s, r) => s + Number(r.sold), 0)
  const totalPendingSupply  = genUnitRows.reduce((s, r) => s + Number(r.pending), 0)
  const totalPendingDemand  = consUnitRows.reduce((s, r) => s + Number(r.pending), 0)

  // ── Lead Pipeline (real, from leadStore) ──────────────────────────────
  const STAGE_DEFS = [
    { key: LeadStage.HOT,  label: 'Hot',  color: '#ef4444' },
    { key: LeadStage.WARM, label: 'Warm', color: '#f59e0b' },
    { key: LeadStage.COLD, label: 'Cold', color: '#94a3b8' },
    { key: LeadStage.LOST, label: 'Lost', color: '#64748b' },
    { key: LeadStage.WON,  label: 'Won',  color: '#10b981' },
  ]
  const pipeline = STAGE_DEFS.map(sd => {
    const inStage = leads.filter(l => l.stage === sd.key)
    return {
      stage: sd.label,
      count: inStage.length,
      value: inStage.reduce((s, l) => s + leadValue(l), 0),
      color: sd.color,
      pct: totalLeads ? Math.round((inStage.length / totalLeads) * 100) : 0,
    }
  })

  // ── Generation Trend (period-aware + plant-aware, from real records) ──
  // Buckets are driven by the Power Type master (powerTypes), not a hardcoded
  // list — whatever types are marked active there, and actually used by a
  // generator/plant, get their own trend bucket/card.
  const activePowerTypes = powerTypes.filter(pt => pt.status === 'active')
  const slugKey = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, '-')

  // Classify a single free-typed power-type label against the master by name.
  const classifyOne = (t?: string): string | null => {
    const s = (t || '').trim().toLowerCase()
    if (!s) return null
    const match = activePowerTypes.find(pt => {
      const n = pt.name.trim().toLowerCase()
      return !!n && (s.includes(n) || n.includes(s))
    })
    return match ? match.name : null
  }
  const bucketsOf = (types: string[] = []): string[] => {
    const set = new Set<string>()
    types.forEach(t => { const b = classifyOne(t); if (b) set.add(b) })
    return [...set]
  }
  // Split one generator reading across its PLANTS by proposed-annual-generation
  // share, so a hybrid (e.g. Solar + Wind) is divided correctly instead of
  // counted twice in full.
  const attributeUnits = (gen: typeof generators[number], units: number): Record<string, number> => {
    const out: Record<string, number> = {}
    const plants = (gen.plants || []).filter(p => p.status === 'active')
    if (plants.length > 0) {
      const capTotal = plants.reduce((s, p) => s + (Number(p.capacity) || 0), 0)
      plants.forEach(p => {
        const share = capTotal > 0 ? (Number(p.capacity) || 0) / capTotal : 1 / plants.length
        const bs = bucketsOf(p.typeOfPower)
        if (!bs.length) return
        const per = (units * share) / bs.length
        bs.forEach(b => { out[b] = (out[b] || 0) + per })
      })
    } else {
      // Generator with no plant breakdown — fall back to its summary type.
      const bs = bucketsOf(gen.typeOfPower)
      if (bs.length) { const per = units / bs.length; bs.forEach(b => { out[b] = (out[b] || 0) + per }) }
    }
    return out
  }
  // Which master power types are actually used by at least one active
  // generator/plant — this is what determines which cards render, so the
  // dashboard only ever shows sources that generators/consumers actually use.
  const usedPowerTypeNames = (() => {
    const set = new Set<string>()
    generators.filter(g => g.status === 'active').forEach(g => {
      const plants = (g.plants || []).filter(p => p.status === 'active')
      if (plants.length > 0) {
        plants.forEach(p => bucketsOf(p.typeOfPower).forEach(b => set.add(b)))
      } else {
        bucketsOf(g.typeOfPower).forEach(b => set.add(b))
      }
    })
    return set
  })()
  const activeGenPowerTypes = activePowerTypes.filter(pt => usedPowerTypeNames.has(pt.name))

  // Time buckets across the selected period — daily for short windows, monthly
  // for long ones (same bucketing as the Revenue vs Expenses trend below).
  const trendByMonth = dashRange[1].diff(dashRange[0], 'day') > 62
  const trendUnit: 'month' | 'day' = trendByMonth ? 'month' : 'day'
  const trendLabel = (d: Dayjs) => (trendByMonth ? d.format('MMM') : d.format('DD MMM'))
  const trendBucketKey = (d: Dayjs) => d.startOf(trendUnit).format('YYYY-MM-DD')
  const trendIndex: Record<string, Record<string, any> & { month: string; Allocated: number }> = {}
  const trendOrder: string[] = []
  {
    let cur = dashRange[0].startOf(trendUnit)
    let guard = 0
    while ((cur.isBefore(dashRange[1]) || cur.isSame(dashRange[1], trendUnit)) && guard++ < 400) {
      const key = cur.format('YYYY-MM-DD')
      const slot: Record<string, any> = { month: trendLabel(cur), Allocated: 0 }
      activeGenPowerTypes.forEach(pt => { slot[pt.name] = 0 })
      trendIndex[key] = slot as any
      trendOrder.push(key)
      cur = cur.add(1, trendUnit)
    }
  }
  // Generation by source — only generators matching the Generation Type filter.
  genReadings.forEach(r => {
    const gen = matchingGenerators.find(g => g.id === r.entityId)
    if (!gen) return
    const slot = trendIndex[trendBucketKey(dayjs(r.startDate))]
    if (!slot) return
    const a = attributeUnits(gen, Number(r.totalUnits) || 0)
    Object.keys(a).forEach(k => { slot[k] = (slot[k] || 0) + a[k] })
  })
  // Allocated units — respect both the Plant/Generator and Generation Type
  // filters via the allocation's generator (same noEntityFilter gate as the
  // invoices/payouts filters above, for the same reason).
  allocInRange.forEach(al => {
    if (!noEntityFilter && !matchingGenerators.some(g => g.id === al.generatorId)) return
    const d = allocDate(al.period)
    if (!d) return
    const slot = trendIndex[trendBucketKey(d)]
    if (slot) slot.Allocated += Number(al.totalAllocated) || 0
  })
  const trendData = trendOrder.map(k => trendIndex[k])
  const trendTotals: Record<string, number> = { Allocated: trendData.reduce((s, d) => s + d.Allocated, 0) }
  activeGenPowerTypes.forEach(pt => {
    trendTotals[pt.name] = trendData.reduce((s, d) => s + (Number(d[pt.name]) || 0), 0)
  })
  // Card definitions for the per-source summary (sparkline data pulled from trendData) —
  // one card per master power type actually in use by a generator, plus Allocated.
  const POWER_TYPE_PALETTE = ['#F59E0B', '#0EA5E9', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899', '#84CC16', '#F97316', '#6366F1', '#06B6D4']
  const POWER_TYPE_ICONS: Record<string, string> = { solar: '☀️', wind: '💨', thermal: '🔥', biomass: '🌾', coal: '🔥', hybrid: '🔀', hydro: '💧', iex: '📈' }
  const iconFor = (name: string) => POWER_TYPE_ICONS[name.toLowerCase()] || '⚡'
  const genTrendCards = [
    ...activeGenPowerTypes.map((pt, i) => ({
      key: pt.name,
      title: `${pt.name} Generation Trend`,
      color: POWER_TYPE_PALETTE[i % POWER_TYPE_PALETTE.length],
      icon: iconFor(pt.name),
      sub: 'kWh generated',
    })),
    { key: 'Allocated', title: 'Allocated Units Trend', color: '#10B981', icon: '⚡', sub: 'kWh allocated' },
  ]
  // ── Recent Leads (real, newest first) ─────────────────────────────────
  const recentLeads = [...leads]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 6)
    .map(l => ({
      key: l.id,
      id: l.id,
      company: l.companyName,
      customer: l.customerName,
      city: l.city,
      kwp: l.sanctionedDemand,
      stage: stageKey(l.stage),
      value: leadValue(l),
      assignee: l.assignedTo || 'Unassigned',
      age: ageDays(l.createdAt),
    }))

  // ── Pending Approvals (real: proposals + payouts + expenses awaiting action) ──
  const pendingApprovals = [
    ...proposalsAll
      .filter(p => p.status === ProposalStatus.DRAFT || p.status === ProposalStatus.SUBMITTED || p.status === ProposalStatus.UNDER_REVIEW)
      .map(p => ({ key: p.id, id: p.referenceNumber || p.id, type: 'Proposal', entity: p.leadName || p.leadId, amount: null as number | null, submittedBy: 'Sales', daysAgo: ageDays(p.createdAt), priority: 'high' })),
    ...payouts
      .filter(po => po.status === 'pending')
      .map(po => ({ key: po.id, id: po.payoutNumber, type: 'Payout', entity: po.generatorName, amount: po.netPayout as number | null, submittedBy: 'Finance', daysAgo: ageDays(po.createdAt), priority: 'medium' })),
  ]
  const approvalsByPriority = {
    high:   pendingApprovals.filter(a => a.priority === 'high').length,
    medium: pendingApprovals.filter(a => a.priority === 'medium').length,
    low:    pendingApprovals.filter(a => a.priority === 'low').length,
  }

  // ── Outstanding Payments (real: unpaid invoices) ──────────────────────
  const outstandingPayments = invoices
    .filter(i => (i.paidAmount || 0) < (i.totalAmount || 0))
    .map(i => {
      const dueDiff = Math.round((now.valueOf() - dayjs(i.dueDate).valueOf()) / 86_400_000)
      return {
        key: i.id,
        invoice: i.invoiceNumber,
        company: i.entity,
        amount: (i.totalAmount || 0) - (i.paidAmount || 0),
        due: i.dueDate,
        overdue: dueDiff,
        status: dueDiff > 0 ? 'overdue' : 'upcoming',
      }
    })

  // ── Top Performers (real: employees ranked by their assigned-lead value,
  // scoped to the selected Period like the rest of the dashboard) ──
  const topPerformers = employees
    .map(e => {
      const eLeads = leads.filter(l => l.assignedTo === e.name && inDashRange(l.createdAt))
      const converted = eLeads.filter(l => l.convertedToProposal || l.stage === LeadStage.WON).length
      return {
        name: e.name,
        revenue: eLeads.reduce((s, l) => s + leadValue(l), 0),
        leads: eLeads.length,
        converted,
        target: eLeads.length ? Math.min(100, Math.round((converted / eLeads.length) * 100) || eLeads.length * 40) : 0,
        avatar: e.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
      }
    })
    .filter(p => p.leads > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 4)

  // ── Trend (Revenue) — real, bucketed within the selected period ──
  const monthlyTrend = (() => {
    const [start, end] = dashRange
    const byMonth = end.diff(start, 'day') > 62
    const unit: 'month' | 'day' = byMonth ? 'month' : 'day'
    const label = (d: Dayjs) => (byMonth ? d.format('MMM') : d.format('DD MMM'))
    const bucketKey = (d: Dayjs) => d.startOf(unit).format('YYYY-MM-DD')
    const map: Record<string, { month: string; revenue: number }> = {}
    let cur = start.startOf(unit)
    // cap the number of buckets so very long custom ranges stay readable
    let guard = 0
    while ((cur.isBefore(end) || cur.isSame(end, unit)) && guard++ < 400) {
      map[cur.format('YYYY-MM-DD')] = { month: label(cur), revenue: 0 }
      cur = cur.add(1, unit)
    }
    invoices.forEach(i => {
      const k = bucketKey(dayjs(i.issueDate || i.dueDate))
      if (map[k]) map[k].revenue += Number(i.totalAmount) || 0
    })
    return Object.values(map)
  })()

  // ── Recent Activities — real, newest first across leads/proposals/invoices/agreements ──
  const relTime = (d: Dayjs) => {
    const mins = now.diff(d, 'minute')
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    const hrs = now.diff(d, 'hour')
    if (hrs < 24) return `${hrs} hr ago`
    const days = now.diff(d, 'day')
    return days < 30 ? `${days}d ago` : d.format('DD MMM YY')
  }
  const activityFeed: { ts: Dayjs; event: string; actor: string; type: string; color: string }[] = []
  leads.forEach(l => activityFeed.push({ ts: dayjs(l.createdAt), event: `New lead: ${l.companyName || l.customerName || l.id}`, actor: l.assignedTo || 'Unassigned', type: 'lead', color: '#6366f1' }))
  proposalsAll.forEach(p => activityFeed.push({ ts: dayjs(p.createdAt), event: `Proposal ${p.referenceNumber || p.id} — ${p.status}`, actor: p.leadName || 'Sales', type: 'proposal', color: '#0ea5e9' }))
  invoices.forEach(i => activityFeed.push({ ts: dayjs(i.issueDate || i.dueDate), event: `Invoice ${i.invoiceNumber} generated`, actor: 'Finance', type: 'invoice', color: '#10b981' }))
  agreements.forEach(a => { const d = (a as any).createdAt || (a as any).signedDate; if (d) activityFeed.push({ ts: dayjs(d), event: `Agreement ${a.id} — ${(a as any).status || ''}`, actor: 'Admin', type: 'agreement', color: '#ef4444' }) })
  const activities = activityFeed
    .filter(a => a.ts.isValid())
    .sort((x, y) => y.ts.valueOf() - x.ts.valueOf())
    .slice(0, 8)
    .map(a => ({ time: relTime(a.ts), event: a.event, actor: a.actor, type: a.type, color: a.color }))

  // ──────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────
  const fmt = (v: number) =>
    v >= 10000000
      ? `${(v / 10000000).toFixed(2)} Cr`
      : v >= 100000
      ? `${(v / 100000).toFixed(1)} L`
      : v.toLocaleString('en-IN')

  // Energy units: show MWh when large, else kWh
  const fmtUnits = (kwh: number) =>
    kwh >= 1000
      ? `${(kwh / 1000).toLocaleString('en-IN', { maximumFractionDigits: 1 })} MWh`
      : `${kwh.toLocaleString('en-IN')} kWh`

  // Completion → friendly status chip (higher done % = healthier)
  const unitStatus = (donePct: number) =>
    donePct >= 85 ? { label: 'On track', color: '#059669', bg: '#d1fae5' }
    : donePct >= 60 ? { label: 'In progress', color: '#d97706', bg: '#fef3c7' }
    : { label: 'Action needed', color: '#dc2626', bg: '#fee2e2' }

  const stageTag = (stage: string) => {
    const map: any = {
      cold:      { color: '#64748b', bg: '#f1f5f9', label: 'Cold' },
      warm:      { color: '#d97706', bg: '#fef3c7', label: 'Warm' },
      hot:       { color: '#dc2626', bg: '#fee2e2', label: 'Hot 🔥' },
      converted: { color: '#059669', bg: '#d1fae5', label: 'Converted' },
      lost:      { color: '#9ca3af', bg: '#f3f4f6', label: 'Lost' },
    }
    const s = map[stage] || map.cold
    return (
      <span className="dash-stage-tag" style={{ color: s.color, background: s.bg }}>
        {s.label}
      </span>
    )
  }

  const priorityDot = (p: string) => {
    const c: any = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' }
    return <span className="dash-priority-dot" style={{ background: c[p] || '#94a3b8' }} />
  }

  // ──────────────────────────────────────────────────────────────────────
  // Column Defs
  // ──────────────────────────────────────────────────────────────────────
  const leadColumns = [
    {
      title: 'Lead',
      key: 'lead',
      render: (_: any, r: any) => (
        <div>
          <div className="dash-cell-primary">{r.company}</div>
          <div className="dash-cell-secondary">{r.id} · {r.customer}</div>
        </div>
      ),
    },
    { title: 'City', dataIndex: 'city', key: 'city', render: (v: string) => <span className="dash-cell-secondary">{v}</span> },
    { title: 'kWp', dataIndex: 'kwp', key: 'kwp', render: (v: number) => <span className="dash-cell-primary">{v} kW</span> },
    { title: 'Value', dataIndex: 'value', key: 'value', render: (v: number) => <span className="dash-value">₹{fmt(v)}</span> },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: stageTag },
    { title: 'Assignee', dataIndex: 'assignee', key: 'assignee', render: (v: string) => <Avatar size={28} className="dash-assignee-avatar">{v[0]}</Avatar> },
    { title: 'Age', dataIndex: 'age', key: 'age', render: (v: number) => <span className="dash-cell-secondary">{v}d</span> },
  ]

  const approvalColumns = [
    {
      title: 'Request',
      key: 'req',
      render: (_: any, r: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {priorityDot(r.priority)}
          <div>
            <div className="dash-cell-primary">{r.entity}</div>
            <div className="dash-cell-secondary">{r.id}</div>
          </div>
        </div>
      ),
    },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (t: string) => <span className="dash-type-chip">{t}</span> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number | null) => v ? <span className="dash-value">₹{fmt(v)}</span> : <span className="dash-cell-secondary">—</span> },
    { title: 'By', dataIndex: 'submittedBy', key: 'by', render: (v: string) => <span className="dash-cell-secondary">{v}</span> },
    { title: 'Age', dataIndex: 'daysAgo', key: 'age', render: (v: number) => <span className="dash-cell-secondary">{v === 0 ? 'Today' : `${v}d ago`}</span> },
    {
      title: '',
      key: 'action',
      render: (_: any, r: any) => (
        <Button
          type="primary"
          size="small"
          className="dash-review-btn"
          onClick={() => navigate(r.type === 'Payout' ? `/payouts/${r.key}/edit` : `/proposals/${r.key}`)}
        >
          Review
        </Button>
      ),
    },
  ]

  const outstandingColumns = [
    {
      title: 'Invoice',
      key: 'inv',
      render: (_: any, r: any) => (
        <div>
          <div className="dash-cell-primary">{r.company}</div>
          <div className="dash-cell-secondary">{r.invoice}</div>
        </div>
      ),
    },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number) => <span className="dash-value">₹{fmt(v)}</span> },
    { title: 'Due Date', dataIndex: 'due', key: 'due', render: (v: string) => <span className="dash-cell-secondary">{v}</span> },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string, r: any) =>
        s === 'overdue' ? (
          <span className="dash-stage-tag" style={{ color: '#dc2626', background: '#fee2e2' }}>
            Overdue {r.overdue}d
          </span>
        ) : (
          <span className="dash-stage-tag" style={{ color: '#059669', background: '#d1fae5' }}>
            Due in {Math.abs(r.overdue)}d
          </span>
        ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, r: any) => (
        <Button size="small" className="dash-link-btn" onClick={() => navigate(`/invoices/${r.key}`)}>
          Send Reminder
        </Button>
      ),
    },
  ]

  // ──────────────────────────────────────────────────────────────────────
  // Analytics Tab Content
  // ──────────────────────────────────────────────────────────────────────
  const analyticsTabItems = [
    {
      key: 'overview',
      label: (
        <span className="dash-tab-label">
          <BarChartOutlined /> Overview
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          {/* Revenue Bar Chart */}
          <div className="dash-chart-section">
            <div className="dash-chart-header">
              <div>
                <h3 className="dash-chart-title">Revenue Trend</h3>
                <p className="dash-chart-sub">
                  {periodMode === 'fy' ? `Monthly trend — ${periodTitle}`
                    : periodMode === 'custom' ? `Trend — ${periodTitle}`
                    : 'Weekly trend — this month'}
                </p>
              </div>
              <div className="dash-legend">
                <span className="dash-legend-item"><span className="dash-legend-dot" style={{ background: '#6366f1' }} />Revenue</span>
              </div>
            </div>
            <div style={{ height: 260, width: '100%', marginTop: 24 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e8f0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#8a93a6' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#8a93a6' }} />
                  <RechartsTooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="dash-chart-section" style={{ marginTop: 24 }}>
            <h3 className="dash-chart-title">Lead Pipeline Funnel</h3>
            <div className="dash-funnel">
              {pipeline.map((p, i) => (
                <div key={p.stage} className="dash-funnel-row">
                  <div className="dash-funnel-label">
                    <span className="dash-funnel-stage">{p.stage}</span>
                    <span className="dash-funnel-count">{p.count} leads</span>
                  </div>
                  <div className="dash-funnel-bar-wrap">
                    <div
                      className="dash-funnel-bar"
                      style={{
                        width: `${p.pct}%`,
                        background: p.color,
                        maxWidth: `${100 - i * 8}%`,
                      }}
                    />
                  </div>
                  <div className="dash-funnel-value">
                    {p.value > 0 ? `₹${fmt(p.value)}` : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'leads',
      label: (
        <span className="dash-tab-label">
          <FireOutlined /> Leads
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          {/* Lead Stats Row */}
          <div className="dash-mini-stats">
            {[
              { label: 'Total Leads',    value: totalLeads,           color: '#6366f1' },
              { label: 'Hot Leads',      value: hotLeads,             color: '#ef4444' },
              { label: 'Converted',      value: convertedLeads,       color: '#10b981' },
              { label: 'Conversion Rate',value: `${conversionRate}%`, color: '#f59e0b' },
            ].map((s) => (
              <div key={s.label} className="dash-mini-stat" style={{ borderTopColor: s.color }}>
                <div className="dash-mini-stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="dash-mini-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          {/* Lead Stage Breakdown */}
          <div className="dash-stage-breakdown">
            {pipeline.map((p) => (
              <div key={p.stage} className="dash-stage-card" style={{ borderTopColor: p.color }}>
                <div className="dash-stage-count" style={{ color: p.color }}>{p.count}</div>
                <div className="dash-stage-name">{p.stage}</div>
                <Progress percent={p.pct} showInfo={false} strokeColor={p.color} size="small" />
              </div>
            ))}
          </div>
          <Table
            dataSource={recentLeads}
            columns={leadColumns}
            pagination={{ defaultPageSize: 5, size: 'small' }}
            size="small"
            style={{ marginTop: 20 }}
          />
        </div>
      ),
    },
    {
      key: 'financial',
      label: (
        <span className="dash-tab-label">
          <DollarOutlined /> Financial
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          {/* Financial KPI Grid */}
          <div className="dash-fin-grid">
            {[
              { label: 'Total Invoiced',  value: `₹${fmt(totalInvoiced)}`,    change: `${invoices.length} invoices`, up: true,  color: '#0ea5e9' },
              { label: 'Collected',       value: `₹${fmt(totalCollected)}`,   change: `${collectionRate}% rate`,     up: true,  color: '#10b981' },
              { label: 'Outstanding',     value: `₹${fmt(totalOutstanding)}`, change: `${unpaidCount} unpaid`,       up: false, color: '#f59e0b' },
              { label: 'Overdue (>30d)',  value: `₹${fmt(overdueAmount)}`,    change: 'past due',                    up: false, color: '#ef4444' },
            ].map((f) => (
              <div key={f.label} className="dash-fin-card" style={{ borderLeftColor: f.color }}>
                <div className="dash-fin-label">{f.label}</div>
                <div className="dash-fin-value" style={{ color: f.color }}>{f.value}</div>
                <div className={`dash-fin-change ${f.up ? 'up' : 'down'}`}>
                  {f.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {f.change}
                </div>
              </div>
            ))}
          </div>

          <h3 className="dash-section-title" style={{ marginTop: 20 }}>Outstanding Payments</h3>
          <Table
            dataSource={outstandingPayments}
            columns={outstandingColumns}
            pagination={false}
            size="small"
          />
        </div>
      ),
    },
    {
      key: 'approvals',
      label: (
        <span className="dash-tab-label">
          <ClockCircleOutlined /> Approvals <span className="dash-tab-badge">{pendingApprovals.length}</span>
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          <div className="dash-approval-header">
            <div className="dash-approval-stats">
              <span className="dash-approval-stat red">{approvalsByPriority.high} High Priority</span>
              <span className="dash-approval-stat yellow">{approvalsByPriority.medium} Medium</span>
              <span className="dash-approval-stat green">{approvalsByPriority.low} Low</span>
            </div>
          </div>
          <Table
            dataSource={pendingApprovals}
            columns={approvalColumns}
            pagination={false}
            size="small"
          />
        </div>
      ),
    },
    {
      key: 'team',
      label: (
        <span className="dash-tab-label">
          <TrophyOutlined /> Team Performance
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          <h3 className="dash-section-title">Top Performers — {periodTitle}</h3>
          <div className="dash-performers">
            {topPerformers.map((p, i) => (
              <div key={p.name} className="dash-performer-card">
                <div className="dash-performer-rank">#{i + 1}</div>
                <Avatar size={48} className="dash-performer-avatar">{p.avatar}</Avatar>
                <div className="dash-performer-info">
                  <div className="dash-performer-name">{p.name}</div>
                  <div className="dash-performer-meta">{p.leads} leads · {p.converted} converted</div>
                  <Progress
                    percent={p.target}
                    size="small"
                    strokeColor={i === 0 ? '#f59e0b' : '#6366f1'}
                    showInfo={false}
                    style={{ marginTop: 6 }}
                  />
                  <div className="dash-performer-target">{p.target}% of target</div>
                </div>
                <div className="dash-performer-revenue">₹{fmt(p.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'activity',
      label: (
        <span className="dash-tab-label">
          <CalendarOutlined /> Activity Feed
        </span>
      ),
      children: (
        <div className="dash-tab-content">
          <div className="dash-activity-feed">
            {activities.map((a, i) => (
              <div key={i} className="dash-activity-item">
                <div className="dash-activity-dot" style={{ background: a.color }} />
                <div className="dash-activity-line" />
                <div className="dash-activity-body">
                  <div className="dash-activity-event">{a.event}</div>
                  <div className="dash-activity-meta">
                    <span className="dash-activity-actor">{a.actor}</span>
                    <span className="dash-activity-time">{a.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ]

  // ──────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────
  // Every hook above this point already ran unconditionally (Rules of Hooks
  // — there's a second useEffect further up, so this can't be an early
  // return before that); only the JSX we hand back changes here. Showing a
  // spinner instead of the real layout while data is still loading avoids
  // a flash of ₹0 / empty tables that then jump to the real numbers.
  if (dashboardLoading) {
    // Placeholder shapes in the real cards' own classes (dash-kpi-card,
    // dash-gen-chart-card, dash-analytics-card) so they inherit the actual
    // size/border/shadow — only the content inside is a shimmering
    // Skeleton instead of real numbers, no separate skeleton CSS needed.
    return (
      <div className="dash-root">
        <div className="dash-header">
          <div className="dash-header-left">
            <Skeleton active title={{ width: 260 }} paragraph={{ rows: 1, width: 360 }} />
          </div>
        </div>

        <div className="dash-kpi-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="dash-kpi-card">
              <Skeleton.Avatar active size={44} shape="square" style={{ borderRadius: 12 }} />
              <div className="dash-kpi-body" style={{ width: '100%' }}>
                <Skeleton active title={false} paragraph={{ rows: 2, width: ['70%', '45%'] }} />
              </div>
            </div>
          ))}
        </div>

        <div className="dash-gen-trend-section" style={{ marginTop: 24, marginBottom: 24 }}>
          <Skeleton active title={{ width: 280 }} paragraph={false} />
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Col xs={24} sm={12} xl={6} key={i}>
                <div className="dash-gen-chart-card">
                  <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '40%' }} />
                  <Skeleton.Node active style={{ width: '100%', height: 100, marginTop: 12 }}>
                    {null}
                  </Skeleton.Node>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {[8, 10].map((rows, i) => (
          <div key={i} className="dash-analytics-card" style={{ padding: 20, marginBottom: 16 }}>
            <Skeleton active paragraph={{ rows }} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="dash-root">

      {/* ── Header ── */}
      <div className="dash-header">
        <div className="dash-header-left">
          <h1 className="dash-greet">
            <span className="greet-hi">{greeting}, </span>
            <span className="greet-name">{userName}.</span>
          </h1>
          <div className="dash-greet-sub">
            {now.format('dddd, DD MMM YYYY')} · Here's how Shakthi Power is performing today.
          </div>
        </div>

        {/* ── Top-right: nav buttons then filters below ── */}
        <div className="dash-header-right">
          <div className="dash-top-filters" id="dash-top-filters">
            {/* Period dropdown */}
            <div className="dash-top-filter-item">
              <span className="dash-top-filter-label"><CalendarOutlined /> Period</span>
              <Select
                value={periodMode === 'fy' ? `fy_${selectedFY}` : periodMode}
                onChange={(val: string) => {
                  if (val === 'month' || val === 'custom') {
                    setPeriodMode(val as 'month' | 'custom')
                  } else {
                    setPeriodMode('fy')
                    setSelectedFY(parseInt(val.replace('fy_', ''), 10))
                  }
                }}
                style={{ minWidth: 190 }}
                size="small"
              >
                <Option value="month">Month ({now.format('MMM YYYY')})</Option>
                <Select.OptGroup label="Financial Year">
                  {[-1, 0, 1].map(offset => {
                    const fy = currentFyStart + offset
                    return (
                      <Option key={`fy_${fy}`} value={`fy_${fy}`}>
                        FY {fy}-{String(fy + 1).slice(-2)}
                      </Option>
                    )
                  })}
                </Select.OptGroup>
                <Select.OptGroup label="Custom">
                  <Option value="custom">Custom Date Range</Option>
                </Select.OptGroup>
              </Select>
              {periodMode === 'custom' && (
                <DatePicker.RangePicker
                  value={customRange}
                  onChange={(v) => v && v[0] && v[1] && setCustomRange([v[0], v[1]])}
                  allowClear={false}
                  format="DD MMM YY"
                  size="small"
                  style={{ marginTop: 4 }}
                />
              )}
            </div>

            {/* Dynamic Plant / Generator dropdown */}
            <div className="dash-top-filter-item">
              <span className="dash-top-filter-label"><ThunderboltOutlined /> Plant / Generator</span>
              <Select
                value={plant}
                onChange={setPlant}
                style={{ minWidth: 180 }}
                size="small"
                showSearch
                optionFilterProp="children"
              >
                <Option value="all">All Plants / Generators</Option>
                {generators.filter(g => (g as any).status !== 'inactive').map(g => (
                  <Option key={g.id} value={g.id}>
                    {g.name || (g as any).plantName || g.id}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Generation Type dropdown */}
            <div className="dash-top-filter-item">
              <span className="dash-top-filter-label"><ThunderboltOutlined /> Generation Type</span>
              <Select
                value={powerTypeFilter}
                onChange={setPowerTypeFilter}
                style={{ minWidth: 150 }}
                size="small"
              >
                <Option value="all">All Types</Option>
                {powerTypes.filter(pt => pt.status === 'active').map(pt => (
                  <Option key={pt.id} value={pt.name}>{pt.name}</Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="dash-topbtns">
            <button className="dash-topbtn" onClick={() => navigate('/notifications')}>
              <CheckCircleOutlined /> My Tasks
            </button>
            <button
              className="dash-topbtn dash-topbtn-primary"
              onClick={() => document.getElementById('revenue-analytics')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <EyeOutlined /> Executive View
            </button>
          </div>
        </div>
      </div>

      {/* Showing label */}
      <div className="dash-period-label-bar">
        Showing data for: <b>{periodTitle}</b>
        {plant !== 'all' && <> · Plant: <b>{generators.find(g => g.id === plant)?.name || plant}</b></>}
        {powerTypeFilter !== 'all' && <> · Generation Type: <b>{powerTypeFilter}</b></>}
      </div>

      {/* ── 1. Lead Details & Pipeline Section (FIRST) ── */}
      <div className="dash-lead-overview-section" style={{ marginTop: 20, marginBottom: 24 }}>
        <div className="dash-section-heading" style={{ marginBottom: 16 }}>
          <FireOutlined style={{ color: '#ef4444', marginRight: 8 }} />
          Lead Pipeline & Sales Overview
          <span className="dash-section-heading-sub">
            Live lead metrics, deal stages, and recent business opportunities
          </span>
        </div>

        {/* Lead Mini KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <div className="dash-kpi-card" style={{ borderLeft: '4px solid #6366f1' }}>
              <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                <FireOutlined style={{ color: '#6366f1', fontSize: 20 }} />
              </div>
              <div className="dash-kpi-body">
                <div className="dash-kpi-label">Total Leads</div>
                <div className="dash-kpi-value" style={{ fontSize: 24, color: '#6366f1' }}>{totalLeads}</div>
                <div className="dash-kpi-sub">Total recorded leads</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6}>
            <div className="dash-kpi-card" style={{ borderLeft: '4px solid #ef4444' }}>
              <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <FireOutlined style={{ color: '#ef4444', fontSize: 20 }} />
              </div>
              <div className="dash-kpi-body">
                <div className="dash-kpi-label">Hot Leads</div>
                <div className="dash-kpi-value" style={{ fontSize: 24, color: '#ef4444' }}>{hotLeads}</div>
                <div className="dash-kpi-sub">High intent deals 🔥</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6}>
            <div className="dash-kpi-card" style={{ borderLeft: '4px solid #10b981' }}>
              <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                <CheckCircleOutlined style={{ color: '#10b981', fontSize: 20 }} />
              </div>
              <div className="dash-kpi-body">
                <div className="dash-kpi-label">Converted Leads</div>
                <div className="dash-kpi-value" style={{ fontSize: 24, color: '#10b981' }}>{convertedLeads}</div>
                <div className="dash-kpi-sub">{conversionRate}% conversion rate</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6}>
            <div className="dash-kpi-card" style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                <DollarOutlined style={{ color: '#f59e0b', fontSize: 20 }} />
              </div>
              <div className="dash-kpi-body">
                <div className="dash-kpi-label">Pipeline Value</div>
                <div className="dash-kpi-value" style={{ fontSize: 24, color: '#f59e0b' }}>
                  ₹{fmt(pipeline.reduce((s, p) => s + (p.value || 0), 0))}
                </div>
                <div className="dash-kpi-sub">Estimated opportunity value</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Lead Stage Funnel & Recent Leads Table */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card
              title={<span style={{ fontWeight: 700, fontSize: 15 }}><FireOutlined style={{ color: '#ef4444', marginRight: 6 }} /> Lead Stage Funnel</span>}
              style={{ borderRadius: 12, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
            >
              <div className="dash-funnel">
                {pipeline.map((p, i) => (
                  <div key={p.stage} className="dash-funnel-row">
                    <div className="dash-funnel-label">
                      <span className="dash-funnel-stage">{p.stage}</span>
                      <span className="dash-funnel-count">{p.count} leads</span>
                    </div>
                    <div className="dash-funnel-bar-wrap">
                      <div
                        className="dash-funnel-bar"
                        style={{
                          width: `${p.pct}%`,
                          background: p.color,
                          maxWidth: `${100 - i * 8}%`,
                        }}
                      />
                    </div>
                    <div className="dash-funnel-value">
                      {p.value > 0 ? `₹${fmt(p.value)}` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title={<span style={{ fontWeight: 700, fontSize: 15 }}><TeamOutlined style={{ color: '#6366f1', marginRight: 6 }} /> Recent Leads & Deals</span>}
              extra={<Button type="link" size="small" onClick={() => navigate('/leads')}>View All Leads ({totalLeads})</Button>}
              style={{ borderRadius: 12, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
            >
              <Table
                dataSource={recentLeads}
                columns={leadColumns}
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* ── 2. Financial Overview & Revenue KPIs ── */}
      <div className="dash-section-heading" style={{ marginBottom: 16 }}>
        <DollarOutlined style={{ color: '#10b981', marginRight: 8 }} />
        Financial & Invoicing Overview
        <span className="dash-section-heading-sub">
          Billed revenue, customer collections & outstanding receivables
        </span>
      </div>

      {/* ── Financial KPI Cards ── */}
      <div className="dash-kpi-grid">
        {kpis.map((k) => (
          <div key={k.key} className="dash-kpi-card">
            <div className="dash-kpi-icon-wrap" style={{ background: k.bg }}>
              {k.icon}
            </div>
            <div className="dash-kpi-body">
              <div className="dash-kpi-label">{k.label}</div>
              <div className="dash-kpi-value">
                {k.prefix}
                {fmt(k.value)}
              </div>
              <div className="dash-kpi-footer">
                {k.change ? (
                  <span className={`dash-kpi-change ${k.change >= 0 ? 'up' : 'down'}`}>
                    {k.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(k.change)}%
                  </span>
                ) : null}
                <span className="dash-kpi-sub">{k.sub}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Generation Trend Dashboard (single, consolidated block) ── */}
      <div className="dash-gen-trend-section" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="dash-section-heading">
          <BarChartOutlined style={{ color: '#6366f1', marginRight: 8 }} />
          Generation Trend Dashboard
          <span className="dash-section-heading-sub">
            Units generated by source vs allocated · {periodTitle}
            {powerTypeFilter !== 'all' ? ` · ${powerTypeFilter}` : ''}
          </span>
        </div>

        {/* Per-source summary cards — period total + sparkline, all from live records */}
        <Row gutter={[16, 16]}>
          {genTrendCards.map((c) => {
            const data = trendData.map(d => ({ label: d.month, units: Math.round(d[c.key]) }))
            const total = trendTotals[c.key]
            return (
              <Col xs={24} sm={12} xl={6} key={c.key}>
                <div className="dash-gen-chart-card">
                  <div className="dash-gen-chart-header">
                    <span className="dash-gen-chart-icon">{c.icon}</span>
                    <div>
                      <div className="dash-gen-chart-title">{c.title}</div>
                      <div className="dash-gen-chart-sub">{c.sub} · {periodTitle}</div>
                    </div>
                  </div>
                  <div className="dash-gen-chart-total" style={{ color: c.color }}>
                    {fmtUnits(total)}
                  </div>
                  <ResponsiveContainer width="100%" height={100}>
                    <AreaChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`grad-${slugKey(c.key)}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={c.color} stopOpacity={0.25} />
                          <stop offset="95%" stopColor={c.color} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F6" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#8A93A6' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#8A93A6' }} axisLine={false} tickLine={false} />
                      <RechartsTooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E4E8F0' }}
                        formatter={(v: any) => [`${Number(v).toLocaleString()} kWh`, 'Units']}
                      />
                      <Area type="monotone" dataKey="units" stroke={c.color} strokeWidth={2}
                        fill={`url(#grad-${slugKey(c.key)})`} dot={{ fill: c.color, strokeWidth: 0, r: 3 }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            )
          })}
        </Row>

        {/* Combined comparison of all sources */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginTop: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0B1426' }}>All sources compared</div>
          <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 12 }}>
            {activeGenPowerTypes.length ? activeGenPowerTypes.map(pt => pt.name).join(', ') : 'Generation'} vs allocated units over the period
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {genTrendCards.map(c => (
                  <linearGradient key={c.key} id={`colorAll-${slugKey(c.key)}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={c.color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={c.color} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <XAxis dataKey="month" stroke="#cbd5e1" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#cbd5e1" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v: any) => `${(Number(v)/1000).toFixed(0)}k`} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <RechartsTooltip cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }} formatter={(value: any) => [fmt(Number(value)) + ' Units', '']} />

              {genTrendCards.map(c => (
                <Area key={c.key} type="monotone" dataKey={c.key} name={c.title} stroke={c.color} strokeWidth={2}
                  fillOpacity={1} fill={`url(#colorAll-${slugKey(c.key)})`} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* ── Revenue Analytics (plant / generator / consumer wise, filterable) ── */}
      <div id="revenue-analytics">
        <RevenueAnalytics plantFilter={plant} range={dashRange} periodLabel={periodTitle} />
      </div>

      {/* ── Pending Units for Sale (solar units) ── */}
      <div className="dash-analytics-card" style={{ marginTop: 16, padding: '0 20px 20px' }}>
        <div className="dash-analytics-header" style={{ padding: '18px 0 0' }}>
          <div className="dash-analytics-title-row">
            <ThunderboltOutlined className="dash-analytics-icon" style={{ color: '#001A4D' }} />
            <span className="dash-analytics-title">Pending Units for Sale</span>
          </div>
          <span style={{ fontSize: 12, color: '#8a93a6', fontWeight: 600 }}>{powerTypeFilter === 'all' ? 'All sources' : powerTypeFilter} · {periodTitle}</span>
        </div>

        {/* Summary stats */}
        <div className="dash-pu-summary">
          {[
            { label: 'Total Generated', val: totalGenerated, color: '#6366f1', icon: <ThunderboltOutlined />, sub: 'across generators' },
            { label: 'Sold / Allocated', val: totalSold, color: '#10b981', icon: <CheckCircleOutlined />, sub: `${totalGenerated ? Math.round((totalSold / totalGenerated) * 100) : 0}% of generation` },
            { label: 'Pending to Sell', val: totalPendingSupply, color: '#f59e0b', icon: <ClockCircleOutlined />, sub: 'generator side' },
            { label: 'Pending to Supply', val: totalPendingDemand, color: '#0ea5e9', icon: <AlertOutlined />, sub: 'consumer demand gap' },
          ].map((s) => (
            <div key={s.label} className="dash-pu-stat" style={{ borderLeftColor: s.color }}>
              <div className="dash-pu-stat-icon" style={{ background: s.color }}>{s.icon}</div>
              <div style={{ minWidth: 0 }}>
                <div className="dash-pu-stat-label">{s.label}</div>
                <div className="dash-pu-stat-value" style={{ color: s.color }}>{fmtUnits(s.val)}</div>
                <div className="dash-pu-stat-sub">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <Row gutter={[24, 16]}>
          {/* Generator-wise — units pending to sell */}
          <Col xs={24} lg={12}>
            <h3 className="dash-section-title" style={{ marginBottom: 14 }}>
              <RiseOutlined style={{ color: '#001A4D', marginRight: 6 }} />
              Generator-wise — units pending to sell
            </h3>
            {genUnitRows.map((r) => {
              const soldPct = r.generated ? Math.round((r.sold / r.generated) * 100) : 0
              const st = unitStatus(soldPct)
              return (
                <div key={r.id} className="dash-pu-item">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5, color: '#0B1426' }}>
                      {r.name}<span className="dash-pu-code">{r.id}</span>
                    </span>
                    <span className="dash-pu-chip" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="dash-pu-track">
                      <div className="dash-pu-fill" style={{ width: `${soldPct}%`, background: st.color }} />
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12.5, color: st.color, whiteSpace: 'nowrap' }}>{soldPct}% sold</span>
                  </div>
                  <div className="dash-pu-metrics">
                    <span>Generated <b style={{ color: '#0B1426' }}>{fmtUnits(r.generated)}</b></span>
                    <span>Sold <b style={{ color: '#059669' }}>{fmtUnits(r.sold)}</b></span>
                    <span>Pending <b style={{ color: '#d97706' }}>{fmtUnits(r.pending)}</b></span>
                  </div>
                </div>
              )
            })}
          </Col>

          {/* Consumer-wise — units pending to supply */}
          <Col xs={24} lg={12}>
            <h3 className="dash-section-title" style={{ marginBottom: 14 }}>
              <TeamOutlined style={{ color: '#001A4D', marginRight: 6 }} />
              Consumer-wise — units pending to supply
            </h3>
            {consUnitRows.map((r) => {
              const suppliedPct = r.required ? Math.round((r.received / r.required) * 100) : 0
              const st = unitStatus(suppliedPct)
              return (
                <div key={r.id} className="dash-pu-item">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5, color: '#0B1426' }}>
                      {r.name}<span className="dash-pu-code">{r.id}</span>
                    </span>
                    <span className="dash-pu-chip" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="dash-pu-track">
                      <div className="dash-pu-fill" style={{ width: `${suppliedPct}%`, background: st.color }} />
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12.5, color: st.color, whiteSpace: 'nowrap' }}>{suppliedPct}% supplied</span>
                  </div>
                  <div className="dash-pu-metrics">
                    <span>Required <b style={{ color: '#0B1426' }}>{fmtUnits(r.required)}</b></span>
                    <span>Supplied <b style={{ color: '#0ea5e9' }}>{fmtUnits(r.received)}</b></span>
                    <span>Pending <b style={{ color: '#d97706' }}>{fmtUnits(r.pending)}</b></span>
                  </div>
                </div>
              )
            })}
          </Col>
        </Row>
      </div>

      {/* ── Analytics Tabs ── */}
      <div className="dash-analytics-card">
        <div className="dash-analytics-header">
          <div className="dash-analytics-title-row">
            <BarChartOutlined className="dash-analytics-icon" />
            <span className="dash-analytics-title">Analytics</span>
          </div>
          <Button
            size="small"
            icon={<FilterOutlined />}
            className="dash-icon-btn"
            onClick={() => document.getElementById('dash-top-filters')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          >
            Filter
          </Button>
        </div>
        <Tabs
          activeKey={activeAnalyticsTab}
          onChange={setActiveAnalyticsTab}
          items={analyticsTabItems}
          className="dash-analytics-tabs"
          size="small"
        />
      </div>

      {/* ── Bottom Row ── */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Conversion Rate */}
        <Col xs={24} md={8}>
          <div className="dash-conv-card">
            <h3 className="dash-conv-title">Lead Conversion</h3>
            <div className="dash-conv-circle">
              <Progress
                type="dashboard"
                percent={conversionRate}
                format={(p) => (
                  <div className="dash-conv-format">
                    <div className="dash-conv-pct">{p}%</div>
                    <div className="dash-conv-pct-label">Rate</div>
                  </div>
                )}
                strokeColor={{ '0%': '#6366f1', '100%': '#818cf8' }}
                trailColor="#EEF1F7"
                size={160}
                strokeWidth={10}
              />
            </div>
            <div className="dash-conv-stats">
              <div className="dash-conv-stat">
                <div className="dash-conv-stat-value">{totalLeads}</div>
                <div className="dash-conv-stat-label">Total Leads</div>
              </div>
              <div className="dash-conv-divider" />
              <div className="dash-conv-stat">
                <div className="dash-conv-stat-value" style={{ color: '#10b981' }}>{convertedLeads}</div>
                <div className="dash-conv-stat-label">Converted</div>
              </div>
              <div className="dash-conv-divider" />
              <div className="dash-conv-stat">
                <div className="dash-conv-stat-value" style={{ color: '#ef4444' }}>{lostLeads}</div>
                <div className="dash-conv-stat-label">Lost</div>
              </div>
            </div>
          </div>
        </Col>

        {/* Quick Actions */}
        <Col xs={24} md={8}>
          <div className="dash-quick-card">
            <h3 className="dash-conv-title">Quick Actions</h3>
            <div className="dash-quick-actions">
              {[
                { label: 'New Lead',       icon: <FileTextOutlined />,  path: '/leads/new',      color: '#6366f1' },
                { label: 'Invoices',       icon: <DollarOutlined />,    path: '/invoices',       color: '#0ea5e9' },
                { label: 'View Reports',   icon: <BarChartOutlined />,  path: '/reports/lead-conversion', color: '#10b981' },
                { label: 'Daily DCR',      icon: <CalendarOutlined />,  path: '/dcr',             color: '#f59e0b' },
                { label: 'Meter Reading',  icon: <ThunderboltOutlined />, path: '/meter-readings', color: '#8b5cf6' },
                { label: 'Payments',       icon: <CheckCircleOutlined />, path: '/payments',       color: '#ef4444' },
              ].map((a) => (
                <button
                  key={a.label}
                  className="dash-quick-btn"
                  style={{ '--qa-color': a.color } as any}
                  onClick={() => navigate(a.path)}
                >
                  <span className="dash-quick-btn-icon" style={{ color: a.color }}>{a.icon}</span>
                  <span className="dash-quick-btn-label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Col>

        {/* Activity Summary */}
        <Col xs={24} md={8}>
          <div className="dash-activity-card">
            <h3 className="dash-conv-title">Recent Activity</h3>
            <div className="dash-activity-list">
              {activities.slice(0, 5).map((a, i) => (
                <div key={i} className="dash-activity-row">
                  <div className="dash-activity-bullet" style={{ background: a.color }} />
                  <div className="dash-activity-content">
                    <div className="dash-activity-text">{a.event}</div>
                    <div className="dash-activity-time-label">{a.time}</div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="link" size="small" className="dash-view-all-btn" onClick={() => navigate('/notifications')}>
              View all activity →
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
